import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

export default function LegalPage() {
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState<"privacy" | "terms">(
    "privacy"
  );
  const [activeToc, setActiveToc] = useState("");

  useEffect(() => {
    const sectionParam = searchParams.get("section");
    if (sectionParam === "terms" || sectionParam === "privacy") {
      setActiveSection(sectionParam);
      const targetId =
        sectionParam === "terms" ? "terms-of-service" : "privacy-policy";

      setTimeout(() => {
        scrollToSection(targetId);
      }, 100);
    }
  }, [searchParams]);

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll("section[id]");
      let current = "";

      sections.forEach((section) => {
        const sectionTop = section.getBoundingClientRect().top;
        if (sectionTop <= 150) {
          current = section.getAttribute("id") || "";
        }
      });

      setActiveToc(current);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const privacySections = [
    { id: "privacy-intro", label: "Introduction" },
    { id: "privacy-collect", label: "Information We Collect" },
    { id: "privacy-purpose", label: "Purpose of Use" },
    { id: "privacy-retention", label: "Data Retention" },
    { id: "privacy-sharing", label: "Information Sharing" },
    { id: "privacy-third-party", label: "Third-Party Processing" },
    { id: "privacy-security", label: "Data Security" },
    { id: "privacy-rights", label: "User Rights" },
    { id: "privacy-contact", label: "Contact Information" },
    { id: "privacy-updates", label: "Policy Updates" },
  ];

  const termsSections = [
    { id: "terms-eligibility", label: "Eligibility" },
    { id: "terms-service", label: "Description of the Service" },
    { id: "terms-responsibilities", label: "User Responsibilities" },
    { id: "terms-wallet", label: "Wallet & Blockchain Transactions" },
    { id: "terms-fees", label: "Fees and Transactions" },
    { id: "terms-ip", label: "Intellectual Property" },
    { id: "terms-disclaimer", label: "Disclaimer" },
    { id: "terms-liability", label: "Limitation of Liability" },
    { id: "terms-termination", label: "Termination" },
    { id: "terms-governing", label: "Governing Law" },
    { id: "terms-contact", label: "Contact Information" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50">
        <div className="max-w-7xl mx-auto px-6 h-18 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://static.readdy.ai/image/89e72e31157ed8c1ffd16b56b32d929a/4f08bb819a2073a6e280dcced10ceca7.png"
              alt="Switch Labs Inc."
              className="h-8 w-auto"
            />
            <span className="text-base font-medium text-gray-900">
              Switch Labs Inc.
            </span>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center bg-gray-100 rounded-full p-1">
              <button
                onClick={() => {
                  setActiveSection("privacy");
                  scrollToSection("privacy-policy");
                }}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeSection === "privacy"
                    ? "bg-gray-900 text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Privacy Policy
              </button>
              <button
                onClick={() => {
                  setActiveSection("terms");
                  scrollToSection("terms-of-service");
                }}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeSection === "terms"
                    ? "bg-gray-900 text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Terms of Service
              </button>
            </div>

            <button
              onClick={() => window.print()}
              className="hidden md:flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
            >
              <i className="ri-download-line"></i>
              <span className="whitespace-nowrap">Download PDF</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-12">
            {/* Sidebar Navigation */}
            <aside className="hidden lg:block w-60 flex-shrink-0">
              <div className="sticky top-32">
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
                  <h3 className="text-xs uppercase font-semibold text-gray-400 mb-4 tracking-wide">
                    On This Page
                  </h3>
                  <nav className="space-y-1">
                    {activeSection === "privacy"
                      ? privacySections.map((section) => (
                          <button
                            key={section.id}
                            onClick={() => scrollToSection(section.id)}
                            className={`w-full text-left text-sm py-2 px-3 rounded-md transition-all cursor-pointer ${
                              activeToc === section.id
                                ? "font-semibold text-gray-900 bg-gray-50 border-l-3 border-gray-900"
                                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                            }`}
                          >
                            {section.label}
                          </button>
                        ))
                      : termsSections.map((section) => (
                          <button
                            key={section.id}
                            onClick={() => scrollToSection(section.id)}
                            className={`w-full text-left text-sm py-2 px-3 rounded-md transition-all cursor-pointer ${
                              activeToc === section.id
                                ? "font-semibold text-gray-900 bg-gray-50 border-l-3 border-gray-900"
                                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                            }`}
                          >
                            {section.label}
                          </button>
                        ))}
                  </nav>
                </div>
              </div>
            </aside>

            {/* Content */}
            <div className="flex-1 max-w-3xl">
              {/* Privacy Policy Section */}
              <article id="privacy-policy" className="mb-32">
                <header className="mb-12">
                  <h1 className="text-5xl font-bold text-gray-900 mb-4 tracking-tight">
                    Privacy Policy
                  </h1>
                  <p className="text-sm text-gray-500">
                    Last updated: January 1, 2025
                  </p>
                </header>

                <section id="privacy-intro" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Introduction
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      Welcome to Switch Protocol (the "Service"), operated by
                      Switch Labs Inc. ("we," "us," or "our"). We are committed
                      to protecting your privacy and ensuring the security of
                      your personal information. This Privacy Policy explains
                      how we collect, use, disclose, and safeguard your
                      information when you use our Web3 application,
                      blockchain-based services, and related platforms.
                    </p>
                    <p>
                      By accessing or using the Service, you acknowledge that
                      you have read, understood, and agree to be bound by this
                      Privacy Policy. If you do not agree with the terms of this
                      Privacy Policy, please do not access or use the Service.
                    </p>
                    <p>
                      This Privacy Policy applies to all users of the Switch
                      Protocol, including those who access our services through
                      mobile applications, web interfaces, or any other means.
                      We encourage you to read this policy carefully and contact
                      us if you have any questions.
                    </p>
                  </div>
                </section>

                <section id="privacy-collect" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Information We Collect
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      We collect several types of information to provide and
                      improve our Service. The information we collect includes:
                    </p>

                    <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
                      Personal Information
                    </h3>
                    <p>
                      When you create an account or use our Service, we may
                      collect personal information that you voluntarily provide,
                      including but not limited to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Email address</li>
                      <li>Username or display name</li>
                      <li>Profile information</li>
                      <li>Communication preferences</li>
                      <li>Customer support correspondence</li>
                    </ul>

                    <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
                      Blockchain and Wallet Information
                    </h3>
                    <p>
                      As a Web3 application, we interact with blockchain
                      networks and digital wallets. We may collect:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Public wallet addresses</li>
                      <li>Transaction history and blockchain data</li>
                      <li>Smart contract interactions</li>
                      <li>Digital signatures for authentication</li>
                      <li>
                        Token balances and holdings (publicly available on-chain
                        data)
                      </li>
                    </ul>

                    <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
                      Usage and Technical Information
                    </h3>
                    <p>
                      We automatically collect certain information when you
                      access our Service:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Device information (type, operating system, browser
                        type)
                      </li>
                      <li>IP address and location data</li>
                      <li>Log data and analytics</li>
                      <li>Cookies and similar tracking technologies</li>
                      <li>Usage patterns and preferences</li>
                    </ul>
                  </div>
                </section>

                <section id="privacy-purpose" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Purpose of Use
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      We use the collected information for various purposes,
                      including:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        <strong>Service Provision:</strong> To provide,
                        maintain, and improve our blockchain-based services,
                        including escrow, payments, and identity verification
                      </li>
                      <li>
                        <strong>Transaction Processing:</strong> To facilitate
                        and verify blockchain transactions, smart contract
                        executions, and digital asset transfers
                      </li>
                      <li>
                        <strong>Account Management:</strong> To create and
                        manage your account, authenticate your identity, and
                        provide customer support
                      </li>
                      <li>
                        <strong>Security and Fraud Prevention:</strong> To
                        detect, prevent, and address technical issues,
                        fraudulent activities, and security threats
                      </li>
                      <li>
                        <strong>Communication:</strong> To send you
                        service-related notifications, updates, and respond to
                        your inquiries
                      </li>
                      <li>
                        <strong>Compliance:</strong> To comply with legal
                        obligations, regulatory requirements, and enforce our
                        Terms of Service
                      </li>
                      <li>
                        <strong>Analytics and Improvement:</strong> To analyze
                        usage patterns, improve user experience, and develop new
                        features
                      </li>
                      <li>
                        <strong>Marketing:</strong> With your consent, to send
                        promotional materials and updates about our services
                      </li>
                    </ul>
                  </div>
                </section>

                <section id="privacy-retention" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Data Retention
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      We retain your personal information only for as long as
                      necessary to fulfill the purposes outlined in this Privacy
                      Policy, unless a longer retention period is required or
                      permitted by law.
                    </p>
                    <p>
                      The criteria we use to determine retention periods
                      include:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        The length of time we have an ongoing relationship with
                        you and provide services
                      </li>
                      <li>
                        Whether there is a legal obligation to which we are
                        subject (such as financial record-keeping requirements)
                      </li>
                      <li>
                        Whether retention is advisable in light of our legal
                        position (such as regarding statutes of limitations,
                        litigation, or regulatory investigations)
                      </li>
                    </ul>
                    <p>
                      Please note that blockchain transactions are permanent and
                      immutable by nature. Once recorded on the blockchain,
                      transaction data cannot be deleted or modified. However,
                      we will delete or anonymize your off-chain personal
                      information when it is no longer necessary for the
                      purposes for which it was collected.
                    </p>
                  </div>
                </section>

                <section id="privacy-sharing" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Information Sharing
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      We do not sell, trade, or rent your personal information
                      to third parties. However, we may share your information
                      in the following circumstances:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        <strong>Service Providers:</strong> We may share
                        information with third-party service providers who
                        perform services on our behalf, such as hosting,
                        analytics, customer support, and payment processing
                      </li>
                      <li>
                        <strong>Business Transfers:</strong> In connection with
                        any merger, sale of company assets, financing, or
                        acquisition of all or a portion of our business
                      </li>
                      <li>
                        <strong>Legal Requirements:</strong> When required by
                        law, court order, or governmental authority, or to
                        protect our rights, property, or safety
                      </li>
                      <li>
                        <strong>Consent:</strong> With your explicit consent or
                        at your direction
                      </li>
                      <li>
                        <strong>Blockchain Networks:</strong> Transaction data
                        shared with blockchain networks is publicly accessible
                        and permanent
                      </li>
                    </ul>
                    <p>
                      All third-party service providers are required to maintain
                      the confidentiality and security of your information and
                      are prohibited from using it for any purpose other than
                      providing services to us.
                    </p>
                  </div>
                </section>

                <section id="privacy-third-party" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Third-Party Processing
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      Our Service may integrate with third-party services,
                      including but not limited to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Blockchain networks and protocols</li>
                      <li>Digital wallet providers</li>
                      <li>Payment processors and exchanges</li>
                      <li>Analytics and monitoring services</li>
                      <li>Cloud infrastructure providers</li>
                    </ul>
                    <p>
                      These third parties have their own privacy policies
                      governing the use of information you provide to them. We
                      are not responsible for the privacy practices of these
                      third parties. We encourage you to review their privacy
                      policies before providing any information.
                    </p>
                    <p>
                      When you connect your digital wallet to our Service, you
                      are interacting directly with the wallet provider. We do
                      not have access to your private keys or seed phrases, and
                      we cannot control or reverse blockchain transactions once
                      they are confirmed.
                    </p>
                  </div>
                </section>

                <section id="privacy-security" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Data Security
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      We implement appropriate technical and organizational
                      security measures to protect your personal information
                      against unauthorized access, alteration, disclosure, or
                      destruction. These measures include:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Encryption of data in transit and at rest</li>
                      <li>Regular security assessments and audits</li>
                      <li>Access controls and authentication mechanisms</li>
                      <li>Secure development practices</li>
                      <li>Employee training on data protection</li>
                      <li>Incident response procedures</li>
                    </ul>
                    <p>
                      However, no method of transmission over the Internet or
                      electronic storage is 100% secure. While we strive to use
                      commercially acceptable means to protect your information,
                      we cannot guarantee its absolute security. You are
                      responsible for maintaining the security of your account
                      credentials, private keys, and wallet access.
                    </p>
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-6">
                      <p className="text-sm font-medium text-yellow-800">
                        <strong>Important Security Notice:</strong> Never share
                        your private keys, seed phrases, or wallet passwords
                        with anyone, including our team members. We will never
                        ask for this information.
                      </p>
                    </div>
                  </div>
                </section>

                <section id="privacy-rights" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    User Rights
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      Depending on your location, you may have certain rights
                      regarding your personal information, including:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        <strong>Access:</strong> The right to request access to
                        your personal information
                      </li>
                      <li>
                        <strong>Correction:</strong> The right to request
                        correction of inaccurate or incomplete information
                      </li>
                      <li>
                        <strong>Deletion:</strong> The right to request deletion
                        of your personal information (subject to legal
                        obligations)
                      </li>
                      <li>
                        <strong>Portability:</strong> The right to receive your
                        personal information in a structured, commonly used
                        format
                      </li>
                      <li>
                        <strong>Objection:</strong> The right to object to
                        certain processing of your information
                      </li>
                      <li>
                        <strong>Restriction:</strong> The right to request
                        restriction of processing in certain circumstances
                      </li>
                      <li>
                        <strong>Withdrawal of Consent:</strong> The right to
                        withdraw consent where processing is based on consent
                      </li>
                    </ul>
                    <p>
                      To exercise any of these rights, please contact us using
                      the information provided in the Contact Information
                      section. We will respond to your request within a
                      reasonable timeframe and in accordance with applicable
                      law.
                    </p>
                    <p>
                      Please note that certain information cannot be deleted due
                      to the immutable nature of blockchain technology. On-chain
                      transaction data will remain permanently recorded on the
                      blockchain.
                    </p>
                  </div>
                </section>

                <section id="privacy-contact" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Contact Information
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      If you have any questions, concerns, or requests regarding
                      this Privacy Policy or our data practices, please contact
                      us at:
                    </p>
                    <div className="bg-gray-50 rounded-lg p-6 my-6">
                      <p className="font-semibold text-gray-900 mb-3">
                        Switch Labs Inc.
                      </p>
                      <p className="text-gray-700">
                        Email:{" "}
                        <a
                          href="mailto:hello@switchlabs.ai"
                          className="text-gray-900 underline font-mono"
                        >
                          hello@switchlabs.ai
                        </a>
                      </p>
                      <p className="text-gray-700">
                        Manager:{" "}
                        <a
                          href="mailto:manager@switchlabs.ai"
                          className="text-gray-900 underline font-mono"
                        >
                          manager@switchlabs.ai
                        </a>
                      </p>
                    </div>
                    <p>
                      We will make every effort to respond to your inquiry
                      within 30 days. For urgent security matters, please
                      clearly mark your communication as "URGENT - SECURITY" in
                      the subject line.
                    </p>
                  </div>
                </section>

                <section id="privacy-updates" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Policy Updates
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      We may update this Privacy Policy from time to time to
                      reflect changes in our practices, technology, legal
                      requirements, or other factors. When we make changes, we
                      will:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Update the "Last updated" date at the top of this policy
                      </li>
                      <li>
                        Notify you through the Service or via email if the
                        changes are material
                      </li>
                      <li>Obtain your consent if required by applicable law</li>
                    </ul>
                    <p>
                      We encourage you to review this Privacy Policy
                      periodically to stay informed about how we are protecting
                      your information. Your continued use of the Service after
                      any changes to this Privacy Policy constitutes your
                      acceptance of such changes.
                    </p>
                    <p>
                      Previous versions of this Privacy Policy will be archived
                      and made available upon request.
                    </p>
                  </div>
                </section>
              </article>

              {/* Divider */}
              <div className="flex items-center justify-center my-32">
                <div className="w-60 h-0.5 bg-gray-200"></div>
              </div>

              {/* Terms of Service Section */}
              <article id="terms-of-service" className="mb-20">
                <header className="mb-12">
                  <h1 className="text-5xl font-bold text-gray-900 mb-4 tracking-tight">
                    Terms of Service
                  </h1>
                  <p className="text-sm text-gray-500">
                    Last updated: January 1, 2025
                  </p>
                </header>

                <section id="terms-eligibility" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Eligibility
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      By accessing or using the Switch Protocol and related
                      services (collectively, the "Service"), you represent and
                      warrant that:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        You are at least 18 years of age or the age of legal
                        majority in your jurisdiction
                      </li>
                      <li>
                        You have the legal capacity to enter into binding
                        contracts
                      </li>
                      <li>
                        You are not located in, or a resident of, any
                        jurisdiction where the use of the Service is prohibited
                        by law
                      </li>
                      <li>
                        You are not subject to any sanctions or restrictions
                        that would prohibit your use of the Service
                      </li>
                      <li>
                        Your use of the Service will not violate any applicable
                        laws or regulations
                      </li>
                    </ul>
                    <p>
                      If you are accessing the Service on behalf of an
                      organization, you represent that you have the authority to
                      bind that organization to these Terms of Service.
                    </p>
                    <p>
                      We reserve the right to refuse service, terminate
                      accounts, or cancel transactions at our sole discretion,
                      particularly if we believe you do not meet these
                      eligibility requirements.
                    </p>
                  </div>
                </section>

                <section id="terms-service" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Description of the Service
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      Switch Protocol is a Web3 infrastructure platform that
                      provides:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        <strong>Blockchain-based Escrow Services:</strong>{" "}
                        Secure holding and release of digital assets based on
                        predefined conditions
                      </li>
                      <li>
                        <strong>Payment Processing:</strong> Facilitation of
                        cryptocurrency and digital asset transactions
                      </li>
                      <li>
                        <strong>Identity Verification:</strong> Decentralized
                        identity solutions for secure authentication
                      </li>
                      <li>
                        <strong>Smart Contract Interactions:</strong> Tools and
                        interfaces for interacting with blockchain smart
                        contracts
                      </li>
                      <li>
                        <strong>Digital Asset Management:</strong> Features for
                        managing and tracking digital assets
                      </li>
                    </ul>
                    <p>
                      The Service operates on various blockchain networks and
                      integrates with third-party protocols. We provide the
                      interface and tools, but the underlying blockchain
                      transactions are executed on decentralized networks that
                      we do not control.
                    </p>
                    <p>
                      We reserve the right to modify, suspend, or discontinue
                      any aspect of the Service at any time, with or without
                      notice. We are not liable for any modification,
                      suspension, or discontinuation of the Service.
                    </p>
                  </div>
                </section>

                <section id="terms-responsibilities" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    User Responsibilities
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>As a user of the Service, you agree to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Provide accurate, current, and complete information when
                        creating an account
                      </li>
                      <li>
                        Maintain the security of your account credentials,
                        private keys, and wallet access
                      </li>
                      <li>
                        Notify us immediately of any unauthorized access or
                        security breach
                      </li>
                      <li>
                        Comply with all applicable laws and regulations in your
                        jurisdiction
                      </li>
                      <li>
                        Use the Service only for lawful purposes and in
                        accordance with these Terms
                      </li>
                      <li>
                        Not engage in any activity that could harm, disable, or
                        impair the Service
                      </li>
                      <li>
                        Not attempt to gain unauthorized access to any part of
                        the Service
                      </li>
                      <li>
                        Not use the Service for money laundering, terrorist
                        financing, or other illegal activities
                      </li>
                      <li>
                        Not impersonate any person or entity or misrepresent
                        your affiliation
                      </li>
                      <li>
                        Not interfere with other users' use and enjoyment of the
                        Service
                      </li>
                    </ul>
                    <p>
                      You are solely responsible for all activities that occur
                      under your account. We are not liable for any loss or
                      damage arising from your failure to maintain the security
                      of your account.
                    </p>
                  </div>
                </section>

                <section id="terms-wallet" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Wallet &amp; Blockchain Transactions
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      To use the Service, you must connect a compatible digital
                      wallet. You acknowledge and agree that:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        You are solely responsible for maintaining the security
                        of your wallet and private keys
                      </li>
                      <li>
                        We do not custody, control, or have access to your
                        private keys or wallet funds
                      </li>
                      <li>
                        Blockchain transactions are irreversible once confirmed
                        on the network
                      </li>
                      <li>
                        Transaction fees (gas fees) are determined by the
                        blockchain network, not by us
                      </li>
                      <li>
                        Transaction times and confirmation speeds are dependent
                        on network conditions
                      </li>
                      <li>
                        You are responsible for ensuring sufficient funds and
                        gas fees for transactions
                      </li>
                      <li>
                        Failed transactions may still incur gas fees charged by
                        the network
                      </li>
                      <li>
                        We cannot reverse, cancel, or modify transactions once
                        submitted to the blockchain
                      </li>
                    </ul>
                    <div className="bg-red-50 border-l-4 border-red-400 p-4 my-6">
                      <p className="text-sm font-medium text-red-800">
                        <strong>Critical Warning:</strong> Loss of your private
                        keys or seed phrase will result in permanent loss of
                        access to your digital assets. We cannot recover lost
                        keys or reverse transactions. Always backup your wallet
                        securely.
                      </p>
                    </div>
                    <p>
                      You are responsible for verifying all transaction details
                      before confirming. We are not liable for any losses
                      resulting from incorrect addresses, amounts, or other
                      transaction parameters.
                    </p>
                  </div>
                </section>

                <section id="terms-fees" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Fees and Transactions
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      Use of the Service may be subject to various fees,
                      including:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        <strong>Service Fees:</strong> Fees charged by Switch
                        Protocol for using our services
                      </li>
                      <li>
                        <strong>Network Fees:</strong> Gas fees and transaction
                        costs charged by blockchain networks
                      </li>
                      <li>
                        <strong>Third-Party Fees:</strong> Fees charged by
                        integrated services or protocols
                      </li>
                    </ul>
                    <p>
                      All fees will be clearly disclosed before you complete a
                      transaction. Fees are subject to change at any time, and
                      we will provide notice of material changes when possible.
                    </p>
                    <p>
                      You are responsible for paying all applicable fees. Fees
                      are generally non-refundable, except as required by law or
                      as expressly stated in these Terms.
                    </p>
                    <p>
                      Network fees (gas fees) are paid directly to blockchain
                      validators and are not collected by us. These fees
                      fluctuate based on network demand and are beyond our
                      control.
                    </p>
                  </div>
                </section>

                <section id="terms-ip" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Intellectual Property
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      The Service, including all content, features,
                      functionality, software, and design, is owned by Switch
                      Labs Inc. and is protected by international copyright,
                      trademark, patent, trade secret, and other intellectual
                      property laws.
                    </p>
                    <p>
                      You are granted a limited, non-exclusive,
                      non-transferable, revocable license to access and use the
                      Service for your personal or internal business purposes,
                      subject to these Terms.
                    </p>
                    <p>You may not:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Copy, modify, distribute, sell, or lease any part of the
                        Service
                      </li>
                      <li>
                        Reverse engineer, decompile, or attempt to extract
                        source code
                      </li>
                      <li>
                        Remove or alter any copyright, trademark, or proprietary
                        notices
                      </li>
                      <li>
                        Use our trademarks, logos, or branding without written
                        permission
                      </li>
                      <li>Create derivative works based on the Service</li>
                    </ul>
                    <p>
                      "Switch Protocol," "Switch Labs," and related logos are
                      trademarks of Switch Labs Inc. All other trademarks,
                      service marks, and trade names are the property of their
                      respective owners.
                    </p>
                  </div>
                </section>

                <section id="terms-disclaimer" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Disclaimer
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <div className="bg-red-50 border-2 border-red-400 rounded-lg p-6 my-6">
                      <p className="font-bold text-red-900 mb-4 text-lg">
                        IMPORTANT LEGAL DISCLAIMER
                      </p>
                      <p className="text-red-800 leading-relaxed">
                        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE"
                        WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR
                        IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES
                        OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
                        NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
                      </p>
                    </div>
                    <p>Switch Labs Inc. makes no warranty that:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        The Service will meet your requirements or expectations
                      </li>
                      <li>
                        The Service will be uninterrupted, timely, secure, or
                        error-free
                      </li>
                      <li>
                        The results obtained from using the Service will be
                        accurate or reliable
                      </li>
                      <li>
                        Any errors or defects in the Service will be corrected
                      </li>
                      <li>
                        The Service is free from viruses or other harmful
                        components
                      </li>
                    </ul>
                    <p>
                      We do not provide investment, financial, legal, or tax
                      advice. Any information provided through the Service is
                      for informational purposes only and should not be
                      construed as professional advice. You should consult with
                      appropriate professionals before making any decisions.
                    </p>
                    <p>
                      Blockchain technology and digital assets involve
                      significant risks, including but not limited to price
                      volatility, regulatory uncertainty, technological
                      vulnerabilities, and potential loss of value. You
                      acknowledge and accept these risks.
                    </p>
                    <p>
                      We are not responsible for the actions or omissions of
                      third-party services, blockchain networks, or other users.
                      We do not endorse or guarantee any third-party content,
                      products, or services accessible through the Service.
                    </p>
                  </div>
                </section>

                <section id="terms-liability" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Limitation of Liability
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <div className="bg-red-50 border-2 border-red-400 rounded-lg p-6 my-6">
                      <p className="font-bold text-red-900 mb-4 text-lg">
                        LIMITATION OF LIABILITY
                      </p>
                      <p className="text-red-800 leading-relaxed">
                        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO
                        EVENT SHALL SWITCH LABS INC., ITS AFFILIATES, OFFICERS,
                        DIRECTORS, EMPLOYEES, AGENTS, OR LICENSORS BE LIABLE FOR
                        ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
                        PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF
                        PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE
                        LOSSES, RESULTING FROM:
                      </p>
                    </div>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Your access to or use of (or inability to access or use)
                        the Service
                      </li>
                      <li>
                        Any conduct or content of any third party on the Service
                      </li>
                      <li>Any content obtained from the Service</li>
                      <li>
                        Unauthorized access, use, or alteration of your
                        transmissions or content
                      </li>
                      <li>Loss or theft of digital assets or private keys</li>
                      <li>Blockchain network failures, delays, or errors</li>
                      <li>Smart contract vulnerabilities or exploits</li>
                      <li>Regulatory actions or legal proceedings</li>
                    </ul>
                    <p>
                      WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING
                      NEGLIGENCE), PRODUCT LIABILITY, OR ANY OTHER LEGAL THEORY,
                      AND WHETHER OR NOT WE HAVE BEEN INFORMED OF THE
                      POSSIBILITY OF SUCH DAMAGE.
                    </p>
                    <p>
                      TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, OUR
                      TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR
                      RELATING TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED
                      THE GREATER OF (A) THE AMOUNT YOU PAID TO US IN SERVICE
                      FEES IN THE 12 MONTHS PRECEDING THE CLAIM OR (B) $100 USD.
                    </p>
                    <p>
                      Some jurisdictions do not allow the exclusion or
                      limitation of certain warranties or liabilities. In such
                      jurisdictions, our liability will be limited to the
                      maximum extent permitted by law.
                    </p>
                  </div>
                </section>

                <section id="terms-termination" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Termination
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      We reserve the right to suspend or terminate your access
                      to the Service at any time, with or without cause, with or
                      without notice, effective immediately. Grounds for
                      termination may include, but are not limited to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Violation of these Terms of Service</li>
                      <li>Fraudulent, illegal, or harmful activities</li>
                      <li>
                        Requests by law enforcement or regulatory authorities
                      </li>
                      <li>Unexpected technical issues or security concerns</li>
                      <li>Extended periods of inactivity</li>
                      <li>Non-payment of fees</li>
                    </ul>
                    <p>
                      You may terminate your account at any time by
                      discontinuing use of the Service and contacting us to
                      request account closure.
                    </p>
                    <p>Upon termination:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Your right to access and use the Service will
                        immediately cease
                      </li>
                      <li>
                        You remain responsible for any outstanding fees or
                        obligations
                      </li>
                      <li>
                        We may delete your account data, subject to legal
                        retention requirements
                      </li>
                      <li>
                        Blockchain transactions and on-chain data will remain
                        permanently recorded
                      </li>
                      <li>
                        Provisions of these Terms that by their nature should
                        survive will continue to apply
                      </li>
                    </ul>
                    <p>
                      Termination does not relieve you of any obligations
                      incurred prior to termination, nor does it entitle you to
                      any refund of fees paid.
                    </p>
                  </div>
                </section>

                <section id="terms-governing" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Governing Law
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      These Terms of Service and any dispute or claim arising
                      out of or in connection with them or their subject matter
                      shall be governed by and construed in accordance with the
                      laws of the jurisdiction in which Switch Labs Inc. is
                      incorporated, without regard to its conflict of law
                      provisions.
                    </p>
                    <p>
                      Any legal action or proceeding arising under these Terms
                      will be brought exclusively in the courts located in the
                      jurisdiction of Switch Labs Inc.'s principal place of
                      business, and you hereby irrevocably consent to the
                      personal jurisdiction and venue of such courts.
                    </p>
                    <p>
                      If any provision of these Terms is found to be invalid or
                      unenforceable, the remaining provisions will continue in
                      full force and effect. The failure to enforce any right or
                      provision of these Terms will not constitute a waiver of
                      such right or provision.
                    </p>
                    <p>
                      These Terms constitute the entire agreement between you
                      and Switch Labs Inc. regarding the Service and supersede
                      all prior agreements and understandings, whether written
                      or oral.
                    </p>
                  </div>
                </section>

                <section id="terms-contact" className="mb-16">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Contact Information
                  </h2>
                  <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
                    <p>
                      If you have any questions, concerns, or disputes regarding
                      these Terms of Service, please contact us at:
                    </p>
                    <div className="bg-gray-50 rounded-lg p-6 my-6">
                      <p className="font-semibold text-gray-900 mb-3">
                        Switch Labs Inc.
                      </p>
                      <p className="text-gray-700">
                        Email:{" "}
                        <a
                          href="mailto:hello@switchlabs.ai"
                          className="text-gray-900 underline font-mono"
                        >
                          hello@switchlabs.ai
                        </a>
                      </p>
                      <p className="text-gray-700">
                        Manager:{" "}
                        <a
                          href="mailto:manager@switchlabs.ai"
                          className="text-gray-900 underline font-mono"
                        >
                          manager@switchlabs.ai
                        </a>
                      </p>
                    </div>
                    <p>
                      For legal notices and formal communications, please use
                      the email addresses provided above. We will respond to all
                      inquiries within a reasonable timeframe.
                    </p>
                    <p>
                      By using the Switch Protocol, you acknowledge that you
                      have read, understood, and agree to be bound by these
                      Terms of Service.
                    </p>
                  </div>
                </section>
              </article>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="https://static.readdy.ai/image/89e72e31157ed8c1ffd16b56b32d929a/4f08bb819a2073a6e280dcced10ceca7.png"
                  alt="Switch Labs Inc."
                  className="h-8 w-auto"
                />
                <span className="text-lg font-bold text-gray-900">
                  Switch Labs Inc.
                </span>
              </div>
              <p className="text-sm text-gray-600">
                Web3 Infrastructure for Secure Transactions
              </p>
            </div>

            <div>
              <h3 className="text-xs uppercase font-semibold text-gray-400 mb-4 tracking-wide">
                Contact
              </h3>
              <div className="space-y-2">
                <a
                  href="mailto:hello@switchlabs.ai"
                  className="block text-sm text-gray-700 hover:text-gray-900 hover:underline"
                >
                  hello@switchlabs.ai
                </a>
                <a
                  href="mailto:manager@switchlabs.ai"
                  className="block text-sm text-gray-700 hover:text-gray-900 hover:underline"
                >
                  manager@switchlabs.ai
                </a>
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-500 mb-3">
                © 2025 Switch Labs Inc. All rights reserved.
              </p>
              <div className="flex items-center gap-3 text-xs text-gray-400">
                <button
                  onClick={() => scrollToSection("privacy-policy")}
                  className="hover:text-gray-600 cursor-pointer"
                >
                  Privacy Policy
                </button>
                <span>|</span>
                <button
                  onClick={() => scrollToSection("terms-of-service")}
                  className="hover:text-gray-600 cursor-pointer"
                >
                  Terms of Service
                </button>
              </div>
              <div className="mt-4">
                <a
                  href="https://readdy.ai/?ref=logo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
